/**
 * @fileoverview `@puffin-core/error-handling/strings` — the EN / ES bundles of namespace #57 `errorHandling`, and
 *   `loadErrorHandlingStrings(i18nInstance, locales)`, which registers them on a react-i18next instance.
 * @package @puffin-core/error-handling
 *
 * ## 🔴 LEAF-ONLY, and ESM-ONLY, by design
 *
 * `@puffin-core/i18n-namespaces` imports this module at **app boot**, from every web app, so anything reachable from
 * here lands in every boot chunk. It imports only its own frozen data modules, a zero-import namespace constant and
 * the React-free strings kit: never the package barrel, never a handler.
 *
 * The subpath has no `require` condition: the CommonJS half carries no words (THINKPAD's #57 converge ruling, option
 * (D)). A server reads a code, never a sentence. Its `default` condition names this SAME ESM leaf, the shape every
 * sibling package's `./strings` declares: a resolver that speaks neither `browser` nor `import` (jest's CommonJS runtime
 * reading `@puffin-core/i18n-namespaces`' registries, as the cross-app contracts do) resolves the one leaf instead of
 * failing with "Cannot find module". No CommonJS file carries a word either way.
 *
 * @module @puffin-core/error-handling/strings
 */

import { ERROR_HANDLING_EN } from './en.js';
import { ERROR_HANDLING_ES } from './es.js';
import { ERROR_HANDLING_NAMESPACE, ERROR_HANDLING_NAMESPACE_NUMBER } from './namespace.js';

export {
  ERROR_HANDLING_EN,
  ERROR_HANDLING_ES,
  ERROR_HANDLING_NAMESPACE,
  ERROR_HANDLING_NAMESPACE_NUMBER,
};

const BUNDLES = Object.freeze({ en: ERROR_HANDLING_EN, es: ERROR_HANDLING_ES });

/**
 * The locales this package ships bundles for, DERIVED from `BUNDLES`, never typed out.
 *
 * @constant {ReadonlyArray<string>}
 */
export const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

/**
 * Register one or more of this package's bundles on a react-i18next instance.
 *
 * Idempotent: `addResourceBundle` is called with `overwrite=false`, so a second call changes nothing and a
 * caller-defined override survives.
 *
 * @param {object} i18nInstance - The react-i18next instance.
 * @param {string|string[]} [locales='en'] - Locale code(s) to load.
 * @returns {object} The same instance, for chaining.
 * @throws {Error} If the instance lacks `addResourceBundle`, or a requested locale is unsupported.
 */
export function loadErrorHandlingStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error(
      'loadErrorHandlingStrings: expected a react-i18next instance with addResourceBundle()',
    );
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadErrorHandlingStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Deep merge so an app may add keys; never overwrite, so a caller-defined value wins.
    i18nInstance.addResourceBundle(locale, ERROR_HANDLING_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}

export { resolveString } from './resolveString.js';
